import logger from '@/utils/logger';
import {
  AUTH0_AUDIENCE,
  AUTH0_SCOPE,
  EXPECTED_AUTH_ERRORS,
  MANUAL_REFRESH_METHODS,
  UNRECOVERABLE_AUTH_ERRORS,
  SILENT_AUTH_TIMEOUT_SECONDS,
  getAuth0ClientId,
  getAuth0Domain,
  type LoginMethod,
} from './constants';
import { getRefreshToken, removeAccessToken, setAccessToken, storeTokenResponse } from './tokenStorage';
import type { GetAccessTokenSilentlyFn, RefreshResult } from './types';

let refreshPromise: Promise<RefreshResult> | null = null;

// gloss
export const refreshViaRefreshToken = async (): Promise<RefreshResult> => {
  if (refreshPromise) return refreshPromise;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return { token: null, shouldLogout: false };

  refreshPromise = executeRefresh(refreshToken).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const executeRefresh = async (refreshToken: string): Promise<RefreshResult> => {
  try {
    const response = await fetch(`${getAuth0Domain()}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        client_id: getAuth0ClientId(),
        audience: AUTH0_AUDIENCE,
        scope: AUTH0_SCOPE,
        refresh_token: refreshToken,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      if (data.error === 'invalid_grant') {
        return { token: null, shouldLogout: true };
      }
      logger.warn(`Refresh failed (transient): ${data.error}`, { context: 'tokenRefresh' });
      return { token: null, shouldLogout: false };
    }

    storeTokenResponse(data);
    return { token: data.access_token, shouldLogout: false };
  } catch (error) {
    logger.error(error, { context: 'refreshViaRefreshToken' });
    return { token: null, shouldLogout: false };
  }
};

// gloss
export const refreshViaAuth0Sdk = async (
  getAccessTokenSilently: GetAccessTokenSilentlyFn,
  isAuthenticated: boolean,
): Promise<RefreshResult> => {
  if (!isAuthenticated) return { token: null, shouldLogout: false };

  try {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: AUTH0_AUDIENCE, scope: AUTH0_SCOPE },
      timeoutInSeconds: SILENT_AUTH_TIMEOUT_SECONDS,
    });

    if (token) {
      setAccessToken(token);
      return { token, shouldLogout: false };
    }
  } catch (e: unknown) {
    const error = typeof e === 'object' && e !== null ? (e as Record<string, unknown>) : {};
    const errorCode = typeof error.error === 'string' ? error.error : undefined;
    const errorMessage = typeof error.message === 'string' ? error.message : undefined;
    const isExpected = EXPECTED_AUTH_ERRORS.some(
      (errorType) => errorCode === errorType || errorMessage === errorType,
    );

    if (isExpected) {
      removeAccessToken();
      logger.warn('Silent token error (expected)', { error: errorCode ?? 'unknown' });
      const isUnrecoverable = UNRECOVERABLE_AUTH_ERRORS.some(
        (msg) => errorCode === msg || errorMessage === msg,
      );
      return { token: null, shouldLogout: isUnrecoverable };
    } else {
      logger.error(e, { context: 'silentTokenRetrieval' });
    }
  }

  return { token: null, shouldLogout: false };
};

// gloss
export const usesManualRefresh = (method: LoginMethod | null): boolean => {
  if (!method) return false;
  return (MANUAL_REFRESH_METHODS as readonly string[]).includes(method);
};

// gloss
export const _resetRefreshState = () => {
  refreshPromise = null;
};
